<script setup>
import {
  onMounted,
  ref,
  watch,
  computed,
  inject,
  nextTick, 
} from 'vue'
import { closeOffCanvas as closeBootstrapOffCanvas } from '../utils/offcanvas'
import Footer from './FooterSection.vue'
import { useQuestionsStore } from '../stores/QuestionsStore'
import { useBusinessDaysReminder } from '../composables/useBusinessDaysReminder'
import { storeToRefs } from 'pinia'
import { RemindersCalendarService } from '../services/RemindersCalendarService'
import { useEmployerStore } from '../stores/EmployerStore'
import { useNavigationStore } from '../stores/NavigationStore'
import BcsDynamicText from './BcsDynamicText.vue'
import AlertNotification from './AlertNotification.vue'
import { removeHTMLTags, formatDateMMDDYYYY, sanitizeDateForFilename } from '../utils'
import { RequestService } from '../services/RequestService'
import { AgencyDataService } from '../services/AgencyDataService'
import { isNil } from 'lodash'
import WhiteLabelModal from './WhiteLabelModal.vue'
import { useTemplateVariablesStore } from '../stores/TemplateVariablesStore'
import { isValidHexColor } from '../utils'
import { v4 as uuidv4 } from 'uuid'
import PlanYearDropdown from '@/shared/components/common/PlanYearDropdown.vue'
import { useReportsStore } from '../stores/ReportsStore'
import { useSystemParamsStore } from '../stores/SystemParamsStore'
import { debounce } from 'lodash'
import { derivePlanPdfFields } from '../utils/questionsAndAnswersHelpers'

import ReminderModal from './Reminders/ReminderModal.vue'
import SentRemindersModal from './Reminders/SentRemindersModal.vue'

import { 
  formattedEventDate, 
  formatDateToSendReminder,
  getTodayDate,
  isLeapYear,
  parseLocalDate,
  formatLocalDate
} from './Reminders/dateUtils'
import { withEmployerNameInInvite } from './Reminders/helpers'

const templateStore = useTemplateVariablesStore()
const reportsStore = useReportsStore()
const { getDefinitionById } = templateStore

const emitter = inject('emitter')
const toggleLoadingWithMessage = inject('toggleLoadingWithMessage', () => {})

const { nameMonths, getReminders, getEvents, getEmails, postBatchReminders, getGoogleReminders } =
  RemindersCalendarService()

const questionStore = useQuestionsStore()
const employerStore = useEmployerStore()
const { customNavigation } = useNavigationStore()

const {
  changeLastPlanningSectionGroup,
  isPlanComplete
} = questionStore
const { lastPlanningSectionGroup, planQuestions, isLoading: isLoadingQuestions } = storeToRefs(questionStore)
const { actualEmployer, actualPlanYear, currentPlanYearCopy, userData, actualPlanYearId } = storeToRefs(employerStore)
const { changeLastPlanYear } = employerStore
const { createLog, downloadPdf, handleErrors, getRealtimeDatabaseValue } = RequestService()
const actualMonth = nameMonths[new Date().getMonth()]

const itemsIsSticky = ref(true)
const SCROLL_THRESHOLD_DOWN = 80
const SCROLL_THRESHOLD_UP = 1
const mainContainer = ref(null)
const eventDefinition = ref(null)
const title = ref('')
const weeks = ref(['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5'])
const months = ref()
const beforeMonthsEvents = ref()
const modalReminderSource = ref('')
const sortDirectionEvents = ref('asc')
const events = ref([])
const reminderSendSucess = ref(false)
const recorderReminderSend = ref([])
const googleReminders = ref([])
const justSentReminders = ref([])
const yearOfActualPlan = ref('')
const showRemiderSentModal = ref(false)
const modalRef = ref(null)
const showGooogleReminders = ref(false)
const templateID = ref('')
const actualCalendarType = ref(null)
const recipients = ref([])
const sentRemindersModalRef = ref(null)
const reminderModalRef = ref(null)
const todayDate = ref(getTodayDate())
const selectedEventsInfo = ref([])
const selectedDays = ref('')
const validEmails = ref([])
let isTransitioning = false

const { 
  subtractBusinessDays 
} = useBusinessDaysReminder(eventDefinition, actualPlanYear, yearOfActualPlan)

const planComplete = computed(() => isPlanComplete())

const reminderDaysOption = computed(() => {
  const baseOptions = [
    { value: 0, text: 'Today' },
    { value: 1, text: '1 day before' },
    { value: 3, text: '3 days before' },
    { value: 5, text: '5 days before' },
    { value: 10, text: '10 days before' },
    { value: -1, text: 'Custom date' }
  ]

  return baseOptions.map((option) => {
    return {
      ...option,
      isDisabled: (event) => {
        if ([-1, -2].includes(option.value)) return false
        if (option.value === 0) return false
        
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        
        let dueDate
        if (event.date && typeof event.date === 'string') {
          const [month, day] = event.date.split('/').map(Number)
          
          if (actualPlanYear.value?.plan_start_date && yearOfActualPlan.value) {
            const [planMonth, planDay, planYearNum] = actualPlanYear.value.plan_start_date.split('/').map(Number)
            const planStartDate = new Date(planYearNum, planMonth - 1, planDay)
            
            dueDate = new Date(planYearNum, month - 1, day)
            
            if (dueDate < planStartDate) {
              dueDate.setFullYear(planYearNum + 1)
            }
          } else {
            const currentYear = new Date().getFullYear()
            dueDate = new Date(currentYear, month - 1, day)
            
            if (dueDate < today) {
              dueDate.setFullYear(currentYear + 1)
            }
          }
        } else {
          return false
        }
        
        dueDate.setHours(0, 0, 0, 0)
        
        const reminderDate = subtractBusinessDays(dueDate, option.value)
        return reminderDate < today
       }
     }
    })
})

// Local alias kept for backward-compatibility but delegates to shared util
function closeOffCanvas() {
  try {
    closeBootstrapOffCanvas()
  } catch (error) {
    handleErrors('Error closing off-canvas from bcs calendar:', error)
  }
}


function closeAllModals() {
  try {
    const reminderModal = document.getElementById('addDueDateModal')
    if (reminderModal && reminderModal.classList.contains('show')) {
      // eslint-disable-next-line no-undef
      const modalInstance = bootstrap.Modal.getInstance(reminderModal)
      if (modalInstance) {
        modalInstance.hide()
      }
    }
    
    const sentRemindersModal = document.getElementById('reminders-sent')
    if (sentRemindersModal && sentRemindersModal.classList.contains('show')) {
      // eslint-disable-next-line no-undef
      const modalInstance = bootstrap.Modal.getInstance(sentRemindersModal)
      if (modalInstance) {
        modalInstance.hide()
      }
    }
    
    const backdrops = document.querySelectorAll('.modal-backdrop')
    backdrops.forEach(backdrop => {
      backdrop.remove()
    })
    
    document.body.classList.remove('modal-open')
    document.body.style.overflow = ''
    document.body.style.paddingRight = ''
    document.body.style.removeProperty('overflow')
    document.body.style.removeProperty('padding-right')
    
    if (reminderModalRef.value) {
      reminderModalRef.value.clearModal()
    }
    
    if (sentRemindersModalRef.value) {
      sentRemindersModalRef.value.clearInfo()
    }
    
    showGooogleReminders.value = false
    
  } catch (error) {
    handleErrors('Error closing modals:', error)
  }
}

const planYearChanged = computed(() => {
  let userAlreadyEnterInCalendar = sessionStorage.getItem('userAlreadyEnterInCalendar')
  if (userAlreadyEnterInCalendar === 'true') {
    return actualPlanYear.value?.plan_start_date !== currentPlanYearCopy.value?.plan_start_date
  } else {
    sessionStorage.setItem('userAlreadyEnterInCalendar', 'true')
    return false
  }
})

const calendarTypeChanged = computed(() => {
  const lastCalendarType = sectionType[lastPlanningSectionGroup.value]
  if (isNil(actualCalendarType.value) || isNil(lastCalendarType)) {
    return false
  }
  if (sessionStorage.getItem(`calendarTypeAlertDismissed-${actualPlanYear.value?.id}`) === 'true') {
    return false
  }
  return actualCalendarType.value !== lastCalendarType
})

const filteredEvents = computed(() => {
  let today = new Date()
  today.setHours(0, 0, 0, 0)
  const getEventDate = (eventDateStr, referenceYear, planMonth, planDay) => {
    const [eventMonth, eventDay] = eventDateStr.split('/').map(Number)
    let eventYear = referenceYear

    if (eventMonth < planMonth || (eventMonth === planMonth && eventDay < planDay)) {
      eventYear++
    }

    return new Date(eventYear, eventMonth - 1, eventDay)
  }

  const sortEvents = (eventsArray, planYear, planMonth, planDay) => {
    return eventsArray.sort((a, b) => {
      const dateA = getEventDate(a.date, planYear, planMonth, planDay)
      const dateB = getEventDate(b.date, planYear, planMonth, planDay)
      return sortDirectionEvents.value === 'asc' ? dateA - dateB : dateB - dateA
    })
  }

  if (actualPlanYear.value && actualPlanYear.value.plan_start_date) {
    const [planMonth, planDay, planYear] = actualPlanYear.value.plan_start_date
      .split('/')
      .map(Number)
    const planStartDate = new Date(planYear, planMonth - 1, planDay)
    today = planStartDate >= today ? planStartDate : today

    const filtered = events.value.filter((event) => {
      const eventDate = getEventDate(event.date, planYear, planMonth, planDay)
      return eventDate >= today
    })

    return sortEvents(filtered, planYear, planMonth, planDay)
  } else {
    const filtered = events.value.filter((event) => {
      const [month, day] = event.date.split('/').map(Number)
      const eventDate = new Date(today.getFullYear(), month - 1, day)
      return eventDate >= today
    })

    return sortEvents(filtered, today.getFullYear(), today.getMonth() + 1, today.getDate())
  }
})

const systemParamsStore = useSystemParamsStore()
const titlesOptions = computed(() => systemParamsStore.getGroupTitleLabels())

const sectionType = {
  'LARGE GROUP - SELF INSURED': 2,
  'LARGE GROUPS': 0,
  'SMALL GROUP - SELF INSURED': 1,
  'SMALL GROUP': 3
}

onMounted(async () => {
  templateID.value = await getRealtimeDatabaseValue('SYSTEM_PARAMS/PDF_TEMPLATE_ID/CALENDAR')
  reportsStore.setCalendarTemplateID(templateID.value)
  yearOfActualPlan.value = actualPlanYear.value?.plan_start_date?.split('/')[2] || ''
  actualCalendarType.value = actualPlanYear.value?.groupInsuranceType

  if (actualPlanYear.value?.plan_start_date) {
    await updateMonths()
    updateEvents()
    // Pre-cache PDF once on page entry so the first download is instant
    preCacheCalendarPdf({ footerDisclaimer: userData.value.footerDisclaimer || '' })
  }

  setupModalEventListeners()
  setupSentRemindersModal()

  const handlePopstate = () => {
    closeAllModals()
  }
  
  window.addEventListener('popstate', handlePopstate)
})

// Keep employer basics and derived plan fields cached
watch([
  () => actualEmployer.value,
  () => actualPlanYear.value
], ([emp, plan]) => {
  if (!emp || !plan) return
  reportsStore.setEmployerInfo({
    name: emp.name,
    ein: emp.ein,
    planYear: plan.plan_start_date,
    planYearId: plan.id,
    groupInsuranceType: plan.groupInsuranceType,
  })
}, { immediate: true, deep: true })

watch(() => planQuestions.value, () => {
  try {
    const root = Object.values(planQuestions.value || {})[0]
    if (!root) return
    
    reportsStore.setPlanDerived({
      ...derivePlanPdfFields(root),
      isPlanComplete: isPlanComplete(),
    })
    
  } catch (error) {
    handleErrors('Updating plan derived data', error)
  }
}, { immediate: true, deep: true })

watch(actualPlanYearId, async (id) => {
  if (!id) return
  const result = await getGoogleReminders(id)
  googleReminders.value = result.results || []
}, { immediate: true })

watch(userData, async (data) => {
  const agencyId = data.agencyId
  if (!agencyId) return
  recipients.value = await getEmails(agencyId)
}, { immediate: true })

watch(
  [() => actualPlanYear.value, () => lastPlanningSectionGroup.value],
  async ([newValuePlanYear, newValueLastPlanningSectionGroup]) => {
    if (newValuePlanYear && newValueLastPlanningSectionGroup) {
      await updateMonths()
      updateEvents()
      yearOfActualPlan.value = actualPlanYear.value?.plan_start_date?.split('/')[2] || ''
    }
  }
)

function setupModalEventListeners() {
  nextTick(() => {
    const addDueDateModal = document.getElementById('addDueDateModal')
    if (addDueDateModal) {
      addDueDateModal.addEventListener('show.bs.modal', (event) => {
        const button = event.relatedTarget
        if (button) {
          const source = button.getAttribute('data-source')
          if (source === 'calendar') {
            modalReminderSource.value = 'calendar'
          } else if (source === 'offCanvas') {
            modalReminderSource.value = 'OffCanvas'
            closeOffCanvas()
          }
        }
        if (reminderModalRef.value) {
          reminderModalRef.value.clearModal()
        }
      })

      addDueDateModal.addEventListener('shown.bs.modal', () => {
        if (reminderModalRef.value && reminderModalRef.value.insertDefaultEmails) {
          reminderModalRef.value.insertDefaultEmails()
        }
      })

      addDueDateModal.addEventListener('hidden.bs.modal', () => {
        setTimeout(() => {
          closeAllModals()
        }, 100)
      })
    }
  })
}

function setupSentRemindersModal() {
  nextTick(() => {
    const reminderSendModal = document.getElementById('reminders-sent')
    if (reminderSendModal) {
      reminderSendModal.addEventListener('show.bs.modal', () => {
        if (sentRemindersModalRef.value) {
          sentRemindersModalRef.value.clearInfo()
        }
      })

      reminderSendModal.addEventListener('hidden.bs.modal', () => {
        setTimeout(() => {
          closeAllModals()
        }, 100)
      })
    }
  })
}

const scrollHandler = (e) => {
  // Skip scroll handling during transition to prevent loops
  if (isTransitioning) return
  
  const scrollTop = e.target.scrollTop
  // Hysteresis: different thresholds for expanding/collapsing to prevent loops
  if (itemsIsSticky.value && scrollTop > SCROLL_THRESHOLD_DOWN) {
    isTransitioning = true
    itemsIsSticky.value = false
    setTimeout(() => { 
      isTransitioning = false
      if (mainContainer.value) {
        mainContainer.value.scrollTop += 2
      }
    }, 200)
  } else if (!itemsIsSticky.value && scrollTop < SCROLL_THRESHOLD_UP) {
    isTransitioning = true
    itemsIsSticky.value = true
    setTimeout(() => { isTransitioning = false }, 200)
  }
}

const toggleOffCanvas = (id, definitionExpected, date) => {
  eventDefinition.value = events.value.find(event => 
    event.body === id && event.name === definitionExpected && event.date === date
  ) || { body: id, name: definitionExpected, date: date }
  emitter.emit('toggleOffCanvas', { id, definitionExpected, date })
}

const handleShowGoogleReminders = () => {
  showGooogleReminders.value = true
  justSentReminders.value = []
}

const openReminderModalFromCalendar = () => {
  modalReminderSource.value = 'calendar'
  if (reminderModalRef.value) {
    reminderModalRef.value.clearModal()
  }
}

const handleSendReminders = async (remindersData) => {
  try {    
    const { filteredEvents, eventDefinition: eventDefFromData } = remindersData
    
    justSentReminders.value = []
    recorderReminderSend.value = []
    
    showRemiderSentModal.value = true

    let eventsToProcess = []
    if (remindersData.modalReminderSource === 'OffCanvas') {
      eventsToProcess = [eventDefFromData]
    } else {
      eventsToProcess = filteredEvents || []
    }

    const transformedEmails = [
      ...remindersData.selectedRecipients,
      ...remindersData.validEmails
        .filter((email) => email.trim() !== '')
        .map((email) => ({ email: email.trim() }))
    ].filter((attendee) => attendee.email !== '')

    const remindersForBatch = []
    for (const event of eventsToProcess) {
      let reminderDays
      if (remindersData.modalReminderSource === 'OffCanvas') {
        reminderDays = remindersData.selectedDays
        if (event.customReminder !== null && event.customReminder !== undefined) {
          console.log('event.customReminder', event.customReminder)
          reminderDays = event.customReminder
        }
      } else {
        if (event.reminder === -2 && event.customReminder !== null && event.customReminder !== undefined) {
          console.log('event.customReminder', event.customReminder)
          reminderDays = event.customReminder
        } else if (event.reminder !== '' && event.reminder !== -1 && event.reminder !== -2) {
          reminderDays = event.reminder
        } else {
          reminderDays = 0
        }
      }

      const endDate = formatDateToSendReminder(event.date, actualPlanYear.value, yearOfActualPlan.value)
      const getDescriptionToMultiplesReminders = await getDefinitionById(
        event.body || eventDefinition.value?.body,
        event.name
      )

      const daysToReminder = reminderDays
      const formattedDueDate = formatDateMMDDYYYY(endDate)
      const baseDescription = remindersData.descriptionEvent || getDescriptionToMultiplesReminders.body
      const inviteBody = `<b>Due Date: ${formattedDueDate}</b>\n\n${baseDescription}`
      const completeDescription = withEmployerNameInInvite(actualEmployer.value.name, inviteBody)

      let startDate
      if (daysToReminder == 0) {
        startDate = getTodayDate()
      } else {
        const endDateObj = parseLocalDate(endDate)
        const startDateObj = subtractBusinessDays(endDateObj, daysToReminder)
        console.log('startDateObj', startDateObj)
        startDate = formatLocalDate(startDateObj)
      }

      remindersForBatch.push({
        id: 'bcspro' + uuidv4().toLowerCase().replace(/-/g, ''),
        summary: event.name,
        description: completeDescription,
        end: { date: startDate },
        start: { date: startDate },
        dueDate: endDate
      })
    }

    const batchPayload = {
      reminders: remindersForBatch,
      attendees: transformedEmails,
      planYearId: actualPlanYearId.value,
      employerId: actualEmployer.value.id
    }

    const batchResponse = await postBatchReminders(batchPayload)
    const batchData = batchResponse?.data?.data || {}

    for (const result of (batchData.results || [])) {
      recorderReminderSend.value.push({
        name: result.summary,
        success: result.success
      })
    }

    for (const errorItem of (batchData.errors || [])) {
      recorderReminderSend.value.push({
        name: errorItem.summary,
        success: false
      })
    }

    const allRecipients = [
      ...remindersData.selectedRecipients.map(r => r.email),
      ...remindersData.validEmails
    ].join(', ')

    for (const event of eventsToProcess) {
      const formattedDueDate = formatDateToSendReminder(event.date, actualPlanYear.value, yearOfActualPlan.value)
      let reminderDays = 0
      if (remindersData.modalReminderSource === 'OffCanvas') {
        reminderDays = event.customReminder ?? remindersData.selectedDays
      } else {
        if (event.reminder === -2 && event.customReminder != null) {
          reminderDays = event.customReminder
        } else if (event.reminder !== '' && event.reminder !== -1 && event.reminder !== -2) {
          reminderDays = event.reminder
        }
      }

      let reminderDate
      if (reminderDays === 0) {
        reminderDate = todayDate.value
      } else {
        const dueDateObj = parseLocalDate(formattedDueDate)
        const reminderDateObj = subtractBusinessDays(dueDateObj, reminderDays)
        reminderDate = formatLocalDate(reminderDateObj)
      }

      justSentReminders.value.push({
        id: event.id || Date.now() + Math.random(),
        name: event.name,
        due_date: formattedDueDate,
        start_date: reminderDate,   
        created_at: new Date().toISOString(),
        target_emails: allRecipients,
        body: event.body || remindersData.descriptionEvent
      })
    }

    await createLog({
      logName: 'sent_reminder_log_v2',
      employerId: actualEmployer.value.id,
      planYearId: actualPlanYearId.value,
      agencyId: actualEmployer.value.agency_id,
      origin: 'bcsCalendar',
      role: userData.value.role || '',
      body: batchPayload
    })

    const freshGoogleReminders = (await getGoogleReminders(actualPlanYearId.value)).results || []
    googleReminders.value = freshGoogleReminders
    
    reminderSendSucess.value = true
   
    showGooogleReminders.value = true
    
    if (reminderModalRef.value) {
      reminderModalRef.value.clearModal()
    }
    
    const DUE_DATE_REMINDER_SENT = '4'
    createLog({
      eventId: DUE_DATE_REMINDER_SENT,
      logName: 'user_activity_log_v2',
      employerId: actualEmployer.value.id,
      planYearId: actualPlanYearId.value,
      agencyId: actualEmployer.value.agency_id
    })

    // Mark agency data as updated for Action Center cache invalidation
    const { markAgencyDataUpdated } = AgencyDataService()
    markAgencyDataUpdated(actualEmployer.value.agency_id)
    
  } catch (error) {
    handleErrors('handleSendReminders', error)
    reminderSendSucess.value = false
  }
}

function findEventInCalendar(event, calendar) {
  for (const month in calendar) {
    for (const week in calendar[month]) {
      for (const evt of calendar[month][week]) {
        if (evt.name === event.name) {
          return evt
        }
      }
    }
  }
  return null
}

function compareCalendars(currentCalendar, previousCalendar) {
  for (const month in currentCalendar) {
    for (const week in currentCalendar[month]) {
      for (const event of currentCalendar[month][week]) {
        const prevEvent = findEventInCalendar(event, previousCalendar)
        if (prevEvent && prevEvent.date !== event.date) {
          event.dateChanged = true
        }
      }
    }
  }
  return currentCalendar
}

function removeKeyDateChanged(calendar) {
  for (const month in calendar) {
    for (const week in calendar[month]) {
      for (let event of calendar[month][week]) {
        delete event.dateChanged
      }
    }
  }
}

const updateEvents = async () => {
  let numberStartMonth = parseInt(actualPlanYear.value?.plan_start_date.split('/')[0]) - 1
  events.value = (await getEvents(numberStartMonth, actualPlanYear.value?.groupInsuranceType)).map((event) => ({
    ...event,
    reminder: '',
    selected: false,
    customReminder: null,
    customDate: null,
    showCustom: false
  }))
  // Update calendar context cache
  reportsStore.setCalendarContext({
    events: events.value,
    filteredEvents: filteredEvents.value,
    recipients: recipients.value,
    yearOfActualPlan: yearOfActualPlan.value,
    calendarType: actualCalendarType.value,
  })
  // Pre-cache after events refresh
  preCacheCalendarPdf()
}

const updateMonths = async () => {
  let numberStartMonth = parseInt(actualPlanYear.value?.plan_start_date.split('/')[0]) - 1
  const DEFAULT_TITLE = 'General Compliance Calendar'
  title.value = titlesOptions.value.get(actualPlanYear.value?.groupInsuranceType) || DEFAULT_TITLE
  months.value = await getReminders(numberStartMonth, actualPlanYear.value?.groupInsuranceType)
  getBeforeMonthsEvents()
  reportsStore.setCalendarContext({
    events: events.value,
    filteredEvents: filteredEvents.value,
    recipients: recipients.value,
    yearOfActualPlan: yearOfActualPlan.value,
    calendarType: actualCalendarType.value,
  })
  // Pre-cache after months refresh
  preCacheCalendarPdf()
}

const getBeforeMonthsEvents = async () => {
  if (planYearChanged.value) {
    let numberStartMonth = parseInt(currentPlanYearCopy.value?.plan_start_date.split('/')[0]) - 1
    let beforeSectionGroup = lastPlanningSectionGroup.value
    let type = sectionType[beforeSectionGroup]
    beforeMonthsEvents.value = await getReminders(numberStartMonth, type)
    compareCalendars(months.value, beforeMonthsEvents.value)
  }
}

const scrollTop = () => {
  mainContainer.value.scrollTo({ top: 0, behavior: 'smooth' })
}

function toggleSortEvents() {
  sortDirectionEvents.value = sortDirectionEvents.value === 'asc' ? 'desc' : 'asc'
}

function closeBanner() {
  changeLastPlanYear(actualPlanYear.value)
  removeKeyDateChanged(months.value)
  changeLastPlanningSectionGroup()
  sessionStorage.setItem(`calendarTypeAlertDismissed-${actualPlanYear.value.id}`, 'true')
}

async function exportCalendarToCsv() {
  const formatDueEvents = (events, body) => {
    const ongoingReqs =
      body ?
        body
          .split('<strong>')
          .filter((section, index) => section.trim() && index > 0)
          .map((section) => {
            const [title, description] = section.split('</strong>')
            return {
              title: removeHTMLTags(title).trim(),
              description: removeHTMLTags(description).trim()
            }
          })
      : []

    const eventsWithOngoing = events
      .map((event, index) => {
        const ongoingReq = ongoingReqs[index] || { title: '', description: '' }
        let fullDateString = formatDateToSendReminder(event.date, actualPlanYear.value, yearOfActualPlan.value)

        if (!fullDateString) {
          return {
            event,
            ongoingReq,
            formattedDueDate: event.date || '',
            sortKey: Number.POSITIVE_INFINITY
          }
        }

        // In non-leap years, "2/29" produces an invalid date "YYYY-02-29".
        // Convert to March 1 of the same year (correct compliance behavior).
        if (event.date === '2/29') {
          const resolvedYear = parseInt(fullDateString.split('-')[0], 10)
          if (!isLeapYear(resolvedYear)) {
            fullDateString = `${resolvedYear}-03-01`
          }
        }

        return {
          event,
          ongoingReq,
          formattedDueDate: formatDateMMDDYYYY(fullDateString),
          sortKey: new Date(`${fullDateString}T00:00:00`).getTime()
        }
      })
      .filter(Boolean)
      .sort((a, b) => a.sortKey - b.sortKey)

    return eventsWithOngoing
      .map(({ event, ongoingReq, formattedDueDate }) => {
        return `"${formattedDueDate}","${event.name}","${ongoingReq.title}","${ongoingReq.description}"\n`
      })
      .join('')
  }

  const CSVHeader = `Due Date,Description,Ongoing Requirement Title,Ongoing Requirement Description\n`

  const csvContent =
    CSVHeader +
    formatDueEvents(
      events.value,
      (await getDefinitionById('ongoingRequirementsDefinition', 'Ongoing Requirements'))?.body
    )

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const fileName = `${actualEmployer.value.name} BCS Pro Compliance Calendar Export Plan ${sanitizeDateForFilename(actualPlanYear.value.plan_start_date)}.csv`

  if (navigator.msSaveBlob) {
    navigator.msSaveBlob(blob, fileName)
  } else {
    link.href = URL.createObjectURL(blob)
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

function formatEventsForPdf(monthsObject) {
  const calendarEvents = []
  for (let monthName in monthsObject) {
    const monthEvents = []
    for (let week in monthsObject[monthName]) {
      for (let eventIndex in monthsObject[monthName][week]) {
        let dateRef = monthsObject[monthName][week][eventIndex]
        monthEvents.push({
          date: formattedEventDate(dateRef.date, { weekday: true }, actualPlanYear.value, yearOfActualPlan.value),
          name: dateRef.name
        })
      }
    }
    calendarEvents.push({ month: monthName, events: monthEvents })
  }
  return calendarEvents
}

async function downloadCalendarPdf({ printWithCustomLogo = false }) {
  if (!templateID.value) {
    handleErrors('Calendar', new Error('Template ID not found'))
    return
  }
  closeModalDownload()
  toggleLoadingWithMessage(true, {
    title: 'Downloading calendar...',
    body: 'This could take a minute or two. Thanks for your \n patience while we create the good stuff.'
  })

  const ongoingRequirements = await getDefinitionById(
    'ongoingRequirementsDefinition',
    'Ongoing Requirements'
  )

  const colorValidation = isValidHexColor(userData.value.whitelabelColor)
  if (!colorValidation && printWithCustomLogo) {
    handleErrors('Calendar', new Error('Invalid whitelabel color'))
  }
  const whitelabelColor = printWithCustomLogo && userData.value.whitelabelColor && colorValidation
  ? userData.value.whitelabelColor
  : ''
  // Keep latest calendar context and plan-derived info in cache before building data
  reportsStore.setCalendarTemplateID(templateID.value)
  reportsStore.setCalendarContext({
    events: events.value,
    filteredEvents: filteredEvents.value,
    recipients: recipients.value,
    yearOfActualPlan: yearOfActualPlan.value,
    calendarType: actualCalendarType.value,
  })

  const data = {
    ...reportsStore.calendarPdfData({
      ...reportsStore.reportFooterData({
        agencyInfo: {
          name: userData.value.companyName,
          ein: userData.value.ein,
          is_cap_company: userData.value.CAPCompany,
          location: userData.value.agencyLocation
        },
        footerDisclaimer: userData.value.footerDisclaimer,
        defaultFooterDisclaimer: userData.value.defaultFooterDisclaimer
      }),
      title: title.value,
      evPageCal: formatEventsForPdf(months.value),
      ongoingRequirementsBody: ongoingRequirements.body,
      evDescription: `The ${printWithCustomLogo ? '' : 'BCS Pro'} compliance calendar provides important due dates pertaining to the ${actualEmployer.value.name}'s group health plan. Refer to this calendar throughout the plan year to make sure you stay on top of plan obligations.`,
      printWithCustomLogo,
      whitelabelColor,
      customLogo: userData.value.logoPath || null,
      logoSize: userData.value.logoSize || '',
      isPlanComplete: planComplete.value,
    })
  }

  const CALENDAR_DOWNLOADED = '20'
  const eventData = {
    eventId: CALENDAR_DOWNLOADED,
    logName: 'user_activity_log_v2',
    employerId: actualEmployer.value.id,
    planYearId: actualPlanYearId.value,
    agencyId: actualEmployer.value.agency_id
  }

  const fileName = `BCS Pro ${actualEmployer.value.name} Compliance Calendar for ${sanitizeDateForFilename(actualPlanYear.value.plan_start_date)} Plan Year`
  const cacheKey = buildCacheKey(
    templateID.value,
    fileName,
    data,
    'calendar'
  )

  try {
    await downloadPdf({
      eventData,
      fileName,
      templateID: templateID.value,
      data,
      cacheKey
    })
  } finally {
    // Make sure we always dismiss the overlay (even on cache-hit or errors)
    toggleLoadingWithMessage(false, null)
  }
}

function buildCacheKey(templateId, fileName, payload, prefix = 'calendar') {
  try {
    const canonical = canonicalizeCalendarPayload(payload)
    const str = `${prefix}|${templateId}|${fileName}|${stableStringify(canonical)}`
    let hash = 5381
    for (let i = 0; i < str.length; i++) {
      hash = (hash * 33) ^ str.charCodeAt(i)
    }
    return `${prefix}-${templateId}-${(hash >>> 0).toString(36)}`
  } catch (_) {
    return `${prefix}-${templateId}-${Date.now()}`
  }
}

function canonicalizeCalendarPayload(payload) {
  const clone = JSON.parse(JSON.stringify(payload || {}))
  // Normalize evPageCal ordering deterministically
  if (Array.isArray(clone.evPageCal)) {
    clone.evPageCal = clone.evPageCal
      .map(m => ({
        ...m,
        events: Array.isArray(m.events)
          ? [...m.events].sort((a, b) => {
              const da = String(a?.date || '')
              const db = String(b?.date || '')
              if (da === db) return String(a?.name || '').localeCompare(String(b?.name || ''))
              return da.localeCompare(db)
            })
          : []
      }))
      .sort((a, b) => String(a?.month || '').localeCompare(String(b?.month || '')))
  }
  return clone
}

function stableStringify(obj) {
  const seen = new WeakSet()
  return JSON.stringify(obj, function (key, value) {
    if (value && typeof value === 'object') {
      if (seen.has(value)) return
      seen.add(value)
      if (!Array.isArray(value)) {
        const ordered = {}
        Object.keys(value).sort().forEach(k => {
          ordered[k] = value[k]
        })
        return ordered
      }
    }
    return value
  })
}

async function handleDownloadPdf() {
  if (userData.value.logoPath) {
    openModalDownloadPdf()
    return
  }

  await downloadCalendarPdf({ printWithCustomLogo: false })
}

function openModalDownloadPdf() {
  modalRef.value.openModal()
}

function closeModalDownload() {
  modalRef.value.closeModal()
}

// Pre-cache (background) Calendar PDF to make first download instant
const preCacheCalendarPdf = debounce(async ({ footerDisclaimer = '' } = {}) => {
  try {
    if (!templateID.value || !actualEmployer.value?.name || !actualPlanYear.value?.plan_start_date) return
    const ongoingRequirements = await getDefinitionById(
      'ongoingRequirementsDefinition',
      'Ongoing Requirements'
    )
    const colorValidation = isValidHexColor(userData.value.whitelabelColor)
    const whitelabelColor = userData.value.whitelabelColor && colorValidation ? userData.value.whitelabelColor : ''
    const data = {
      ...reportsStore.calendarPdfData({
        ...reportsStore.reportFooterData({
          agencyInfo: {
            name: userData.value.companyName,
            ein: userData.value.ein,
            is_cap_company: userData.value.CAPCompany,
            location: userData.value.agencyLocation
          },
          footerDisclaimer,
          defaultFooterDisclaimer: userData.value.defaultFooterDisclaimer
        }),
        title: title.value,
        evPageCal: formatEventsForPdf(months.value || {}),
        ongoingRequirementsBody: ongoingRequirements?.body || '',
        evDescription: `The ${userData.value.logoPath ? '' : 'BCS Pro'} compliance calendar provides important due dates pertaining to the ${actualEmployer.value.name}'s group health plan.`,
        printWithCustomLogo: !!userData.value.logoPath,
        whitelabelColor,
        customLogo: userData.value.logoPath || null,
        logoSize: userData.value.logoSize || '',
        isPlanComplete: planComplete.value,
      })
    }
    const fileName = `BCS Pro ${actualEmployer.value.name} Compliance Calendar for ${sanitizeDateForFilename(actualPlanYear.value.plan_start_date)} Plan Year`
    const cacheKey = buildCacheKey(templateID.value, fileName, data, 'calendar')
    await downloadPdf({
      eventData: {},
      fileName,
      templateID: templateID.value,
      data,
      cacheKey,
      precacheOnly: true,
      showLoading: false,
    })
  } catch (error) {
    handleErrors('Pre-caching calendar PDF', error)
  }
}, 1200)
</script>

<template>
  <div v-if="showRemiderSentModal" class="col-10 overlay-white-left">
    <div class="card mt-5 d-flex mx-auto">
      <div
        class="card-body modal-reminder-sent text-center d-flex flex-column justify-content-center align-items-center">
        <h5 class="card-title header-22">
          <i class="bi bi-check-circle icon-created"></i>
          <div class="modal-body-notification-title">
            <b>Reminder Created!</b>
          </div>
        </h5>
        <p class="card-text text-16 text-gray-700">Your selected reminders were sent!</p>
        <button @click="showRemiderSentModal = false" class="btn btn-primary">
          <i class="bi bi-x-circle"></i>
          &nbsp; Close
        </button>
      </div>
    </div>
  </div>

  <div
    class="col-10 px-0 mt-0 scroll-fix"
    id="mainContainer"
    ref="mainContainer"
    @scroll="scrollHandler">
    <div>
      <!-- Warning banner: Plan Questionnaire not complete -->
      <div class="container-fluid px-0 mx-0 mb-0" v-if="!isLoadingQuestions && !planComplete">
        <AlertNotification typeAlert="warning-line" :showCloseButton="false">
          <div class="d-flex align-items-center justify-content-between w-100">
            <span class="text-14 me-2">
              <strong>For a more accurate Compliance Calendar, please fill out the Plan Questionnaire.</strong>                
            </span>
            <a
              class="text-primary text-14 text-decoration-none hand-cursor"
              @click="customNavigation.navigate({ newPage: customNavigation.currentPage, subPage: customNavigation.routes.DetailEmployer.children[1].name })">
              <strong>Go to Plan Questionnaire</strong>
              <i class="bi bi-chevron-right"></i>
            </a>
          </div>
        </AlertNotification>
      </div>

      <div class="mx-4 mb-4">
        <div
          v-if="planYearChanged || calendarTypeChanged"
          class="alert alert-success border border-success py-2 box-shadow-alert alert-dismissible fade show"
          role="alert">
          <i class="bi bi-check-circle-fill"></i>
          <strong>
            &nbsp;The {{ actualEmployer.name }} Calendar has been successfully updated based on
            changes made to the employer information.{{ planYearChanged ? " Changed requirements are highlighted below." : "" }}
          </strong>
          <button
            type="button"
            class="btn-close py-0 pt-4 btn-sm"
            @click="closeBanner()"
            data-bs-dismiss="alert"
            aria-label="Close"></button>
        </div>
      </div>

      <div class="container-fluid pe-0 ps-3.75">
        <div class="calendar-header-sticky bg-white" :class="{ 'pt-0.25 is-compact': !itemsIsSticky }">
          <div class="ps-4" style="padding-right: 10px; margin-bottom: 26px">
            <AlertNotification v-model:showAlert="reminderSendSucess">
              <strong>
                <span style="padding-right: 8px">
                  Calendar reminder has been sent!
                </span>
                <a
                  href=""
                  @click="reminderSendSucess = false"
                  data-bs-toggle="modal"
                  data-bs-target="#reminders-sent">
                  View Reminders Sent ({{ justSentReminders.length }})
                </a>
              </strong>
            </AlertNotification>
          </div>

          <div class="d-flex justify-content-between pb-2" :class="{ 'pt-4.2': itemsIsSticky }">
            <div class="w-100 d-flex flex-column justify-content-end ps-4">
              <div class="d-flex align-items-center">
                <span class="mb-0 text-28 me-3">
                  <med>{{ actualEmployer.name }} Calendar</med>
                </span>
                <div class="align-self-start">
                  <PlanYearDropdown />
                </div>
              </div>
              <p id="group-info" class="mt-1 mb-0 header-22">
                <med>{{ title }}</med>
              </p>
              <p class="mt-2 mb-2 header-22">
                <med>Plan Year: {{ actualPlanYear?.plan_start_date }}</med>
              </p>
              <div class="collapsible-content">
                <p class="py-2 text-14 white-space-pre-wrap mb-0">
                  <BcsDynamicText text="calendarInstructionalText" />
                </p>
              </div>
            </div>
            <div class="w-75 d-flex flex-column align-items-end me-4">
              <div>
                <button id="downloadPdfButtonCalendar" type="button" class="btn btn-info me-2" @click="handleDownloadPdf">
                  <i class="bi bi-download"></i>
                  Download PDF
                </button>
                <button id="exportCsvButtonCalendar" type="button" class="btn btn-primary" @click="exportCalendarToCsv">
                  <i class="bi bi-check2-square"></i>
                  Export CSV
                </button>
              </div>
            </div>
          </div>

          <div class="collapsible-content">
            <div class="w-100 d-flex justify-content-start pb-2 ps-4 me-4 pe-4 align-items-center flex-wrap">
                <div class="mb-3">
                  <button
                    id="addDueDateButtonCalendar"
                    class="btn btn-primary text-14"
                    data-bs-toggle="modal"
                    data-source="calendar"
                    data-bs-target="#addDueDateModal"
                    @click="openReminderModalFromCalendar">
                    <i class="bi bi-calendar-plus-fill"></i>
                    &nbsp;Add Due Date Reminders to External Calendars
                  </button>
                </div>

                <div class="ms-4 mb-3">
                  <button
                    id="viewRemindersSentButtonCalendar"
                    data-bs-toggle="modal"
                    @click="handleShowGoogleReminders"
                    data-bs-target="#reminders-sent"
                    v-if="googleReminders?.length > 0"
                    class="btn btn-info text-14">
                    <i class="bi bi-check2-circle"></i>
                    <strong>&nbsp; View Reminders Sent ( {{ googleReminders?.length }} )</strong>
                  </button>
                </div>
            </div>
          </div>
        </div>
        
        <div class="container-fluid ps-0">
          <div class="row">
            <div class="col-12">
              <table id="calendarTable" class="table table-bordered">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      class="py-3 text-14 text-body-color text-center bg-light width-94">
                      <strong>{{ yearOfActualPlan }}</strong>
                    </th>
                    <th
                      scope="col"
                      class="py-3 text-14 text-body-color text-center bg-light width-214"
                      v-for="week in weeks"
                      :key="week">
                      <strong>{{ week }}</strong>
                    </th>
                  </tr>
                </thead>
                <tbody id="calendarEventsTable">
                  <tr
                    v-for="(dates, month) in months"
                    :key="month"
                    :class="{ 'active-month': month == actualMonth }">
                    <td class="py-1 bg-light min-h-159 text-14 text-body-color">
                      <strong>{{ month }}</strong>
                    </td>
                    <template v-for="i in weeks.length" :key="i">
                      <td
                        class="p-0 vertical-align-top"
                        :class="{
                          'active-month-last-td': month == actualMonth && i == weeks.length
                        }">
                        <template v-if="dates[`Week ${i}`]">
                          <div class="d-flex flex-column min-h-159 p-2">
                            <template v-for="event in dates[`Week ${i}`]" :key="event.name">
                              <div
                                :id="`${event.body}-${event.name}`"
                                @click="toggleOffCanvas(event.body, event.name, event.date)"
                                class="d-flex justify-content-between my-2 text-12 text-primary hand-cursor">
                                <strong class="mb-0">{{ event.name }}</strong>
                                <strong class="ms-3 mb-0">{{ event.date }}</strong>
                              </div>

                              <div
                                v-if="
                                  recorderReminderSend.some(
                                    (reminder) => reminder.name === event?.name && reminder.success
                                  ) ||
                                  googleReminders?.some((reminder) => reminder.name === event?.name)
                                "
                                class="reminder-sent-pill">
                                Reminder Sent
                              </div>

                              <div v-if="event.dateChanged">
                                <small
                                  class="d-inline-flex mb-3 px-2 py-1 text-success-emphasis bg-success-subtle rounded-2 text-12">
                                  <i>Date changed</i>
                                </small>
                              </div>
                            </template>
                          </div>
                        </template>
                        <template v-else>
                          <div
                            class="d-flex flex-column min-h-159"
                            :class="{ 'bg-gray-200': month != actualMonth }"></div>
                        </template>
                      </td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      
      <div class="d-flex flex-column">
        <div class="p-0 position-relative button-top w-0">
          <button
            @click="scrollTop()"
            class="btn btn-primary rounded-circle d-flex justify-content-center align-items-center">
            <div>
              <i class="bi bi-chevron-up position-relative"></i>
              <span class="text-12 position-relative">
                <strong>Top</strong>
              </span>
            </div>
          </button>
        </div>
        <div class="p-0">
          <Footer class="px-4"></Footer>
        </div>
      </div>
    </div>

    <ReminderModal
      ref="reminderModalRef"
      :eventDefinition="eventDefinition"
      :filteredEvents="filteredEvents"
      :modalReminderSource="modalReminderSource"
      :reminderDaysOption="reminderDaysOption"
      :sortDirectionEvents="sortDirectionEvents"
      :recipients="recipients"
      :actualEmployer="actualEmployer"
      :actualPlanYear="actualPlanYear"
      :yearOfActualPlan="yearOfActualPlan"
      @sendReminders="handleSendReminders"
      @toggleSortEvents="toggleSortEvents"
      />

    <SentRemindersModal
      ref="sentRemindersModalRef"
      :googleReminders="justSentReminders.length > 0 ? justSentReminders : googleReminders"
      :showGoogleReminders="showGooogleReminders"
      :sortDirection="sortDirectionEvents"
      :todayDate="todayDate"
      :actualEmployer="actualEmployer"
      :selectedEventsInfo="selectedEventsInfo"
      :eventDefinition="eventDefinition"
      :selectedDays="selectedDays"
      :actualPlanYear="actualPlanYear"
      :yearOfActualPlan="yearOfActualPlan"
      :validEmails="validEmails"
      @toggleSort="toggleSortEvents" />
  </div>
  
  <!-- White label modal -->
  <WhiteLabelModal
    :handleDownload="downloadCalendarPdf"
    :onCloseModal="closeModalDownload"
    :logoSize="userData.logoSize"
    :logoPath="userData.logoPath"
    fileType="PDF"
    ref="modalRef" />
</template>

<style scoped lang="scss">
  .vertical-align-top {
    vertical-align: top;
  }

  .w-0 {
    width: 0;
  }

  .text-body-color {
    color: #212529;
  }

  .width-94 {
    width: 94px;
  }

  .width-214 {
    width: 214px;
  }

  .min-h-159 {
    min-height: 159px;
  }

  .active-month {
    border-left: 4px solid #a193cc;

    td {
      background-color: #f7f1fd80;
      border-top: 1px solid #521b98;
      border-bottom: 1px solid #521b98;
      border-right: 1px solid rgb(222, 226, 230);
      border-left: 1px solid rgb(222, 226, 230);
    }
  }

  .active-month-last-td {
    border-right: 1px solid #521b98 !important;
  }

  .box-shadow-alert {
    box-shadow: 0px 8px 16px 0px #00000026;
  }

  .scroll-fix {
    overflow-y: scroll;
    overflow-x: hidden;
    max-height: 94vh;
  }

  .reminder-sent-pill {
    color: #198754;
    background-color: #d1e7dd;
    border-radius: 4px;
    width: 96px;
    font-size: 12px;
    height: 19px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-style: italic;
  }

  .modal-reminder-sent {
    .icon-created {
      font-size: 50px;
    }

    .card-title {
      color: #198754;
    }
  }

  .hand-cursor {
    cursor: pointer;
  }

  .bg-gray-200 {
    background-color: var(--bs-gray-200);
  }

  .overlay-white-left {
    position: fixed;
  }

  .button-top {
    position: fixed;
    bottom: 30px;
    right: 30px;
    z-index: 1000;
    
    .btn {
      width: 60px;
      height: 60px;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
      
      div {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        
        i {
          font-size: 16px;
          margin-bottom: 2px;
        }
        
        span {
          font-size: 10px;
          line-height: 1;
        }
      }
    }
  }

  .calendar-header-sticky {
    position: sticky;
    top: 0;
    z-index: 100;
    background-color: white !important;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--bs-gray-200);

    .collapsible-content {
      max-height: 200px;
      opacity: 1;
      overflow: hidden;
      transition: max-height 0.2s ease, opacity 0.2s ease;
    }

    &.is-compact .collapsible-content {
      max-height: 0;
      opacity: 0;
    }
  }

  .ps-3\.75 {
    padding-left: 0.9375rem;
  }

  .pt-4\.2 {
    padding-top: 1.05rem;
  }

  .px-4\.5 {
    padding-left: 1.125rem;
    padding-right: 1.125rem;
  }

  .text-12 {
    font-size: 0.75rem;
  }

  .text-14 {
    font-size: 0.875rem;
  }

  .text-16 {
    font-size: 1rem;
  }

  .text-28 {
    font-size: 1.75rem;
  }

  .header-22 {
    font-size: 1.375rem;
    font-weight: 600;
  }

  .white-space-pre-wrap {
    white-space: pre-wrap;
  }

  .text-gray-700 {
    color: #374151;
  }

  .bg-light {
    background-color: #f8f9fa !important;
  }

  .text-primary {
    color: #5c0cb8 !important;
  }

  .text-secondary {
    color: #6c757d !important;
  }

  .text-success-emphasis {
    color: #0a3622 !important;
  }

  .bg-success-subtle {
    background-color: #d1e7dd !important;
  }

  .rounded-2 {
    border-radius: 0.375rem !important;
  }
</style>